import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from './api';

const emptyForm = {
  id: null,
  instrument_code: '',
  version: 1,
  discipline_code: '',
  name: '',
  description: '',
  instrument_type: 'shell',        // shell | licensed | custom
  form_kind: 'instrument',         // instrument | intake
  license_required: 1,             // 0/1
  distribution_mode: 'shell_only', // shell_only | full_content
  license_provider: '',
  is_active: 1,
};

const emptyItemForm = {
  id: null,
  section_id: null,
  item_code: '',
  item_type: 'text',
  label: '',
  prompt_text: '',
  help_text: '',
  required: 0,
  options: '',
  scoring_map: '',
  sort_order: 10,
  is_active: 1,
  external_ref: '',
};

function clsx(...xs) { return xs.filter(Boolean).join(' '); }

function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = 'primary', disabled = false, className = '', type='button' }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

/** scroll-safe modal */
function Modal({ open, title, children, onClose, wide=false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[220] bg-black/40 overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-3 sm:p-6">
        <div className={clsx(
          'bg-white w-full rounded-2xl shadow-xl border',
          wide ? 'max-w-6xl' : 'max-w-2xl',
          'max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] flex flex-col'
        )}>
          <div className="p-4 border-b flex items-center justify-between gap-3 shrink-0">
            <div className="font-semibold text-slate-900">{title}</div>
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
          <div className="p-4 overflow-y-auto">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

function safeJsonString(v) {
  try {
    if (v == null) return '';
    return typeof v === 'string' ? v : JSON.stringify(v, null, 2);
  } catch {
    return '';
  }
}

export default function SuperInstrumentsCatalog() {
  const [loading, setLoading] = useState(false);
  const [formKind, setFormKind] = useState('all'); // all | instrument | intake
  const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });

  // definition builder
  const [defOpen, setDefOpen] = useState(false);
  const [defLoading, setDefLoading] = useState(false);
  const [defInstrument, setDefInstrument] = useState(null);
  const [defSections, setDefSections] = useState([]);
  const [defItems, setDefItems] = useState([]);

  // item edit modal
  const [itemEditOpen, setItemEditOpen] = useState(false);
  const [itemBusy, setItemBusy] = useState(false);
  const [itemForm, setItemForm] = useState({ ...emptyItemForm });

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (formKind !== 'all' && String(it.form_kind) !== String(formKind)) return false;
      if (activeFilter === 'active' && !it.is_active) return false;
      if (activeFilter === 'inactive' && it.is_active) return false;
      if (!t) return true;
      return (
        String(it.instrument_code || '').toLowerCase().includes(t) ||
        String(it.name || '').toLowerCase().includes(t) ||
        String(it.discipline_code || '').toLowerCase().includes(t) ||
        String(it.license_provider || '').toLowerCase().includes(t)
      );
    });
  }, [items, q, formKind, activeFilter]);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (activeFilter === 'active') params.active = 1;
      if (activeFilter === 'inactive') params.active = 0;
      if (formKind !== 'all') params.form_kind = formKind;

      const data = await apiGet('/super/instruments', params);
      setItems(data.items || []);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load global instruments');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, [formKind, activeFilter]); // eslint-disable-line

  function openCreate() {
    setForm({ ...emptyForm });
    setEditOpen(true);
  }

  function openEdit(it) {
    setForm({
      id: it.id,
      instrument_code: it.instrument_code || '',
      version: Number(it.version || 1),
      discipline_code: it.discipline_code || '',
      name: it.name || '',
      description: it.description || '',
      instrument_type: it.instrument_type || 'shell',
      form_kind: it.form_kind || 'instrument',
      license_required: it.license_required ? 1 : 0,
      distribution_mode: it.distribution_mode || 'shell_only',
      license_provider: it.license_provider || '',
      is_active: it.is_active ? 1 : 0,
    });
    setEditOpen(true);
  }

  async function saveInstrument() {
    if (!form.instrument_code.trim() || !form.name.trim()) {
      alert('instrument_code and name are required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        instrument_code: form.instrument_code.trim(),
        version: Number(form.version || 1),
        discipline_code: form.discipline_code.trim() || null,
        name: form.name.trim(),
        description: form.description || null,
        instrument_type: form.instrument_type,
        form_kind: form.form_kind,
        license_required: form.license_required ? 1 : 0,
        distribution_mode: form.distribution_mode,
        license_provider: form.license_provider.trim() || null,
        is_active: form.is_active ? 1 : 0,
      };

      if (!form.id) await apiPost('/super/instruments', payload);
      else await apiPut(`/super/instruments/${form.id}`, payload);

      setEditOpen(false);
      await load();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Save failed');
    } finally {
      setLoading(false);
    }
  }

  async function reloadDefinition() {
    if (!defInstrument?.id) return;
    const data = await apiGet(`/super/instruments/${defInstrument.id}/definition`);
    setDefInstrument(data.instrument || null);
    setDefSections(data.sections || []);
    setDefItems(data.items || []);
  }

  async function openDefinition(it) {
    setDefOpen(true);
    setDefLoading(true);
    try {
      const data = await apiGet(`/super/instruments/${it.id}/definition`);
      setDefInstrument(data.instrument || null);
      setDefSections(data.sections || []);
      setDefItems(data.items || []);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to load definition');
      setDefOpen(false);
    } finally {
      setDefLoading(false);
    }
  }

  async function addSection() {
    if (!defInstrument?.id) return;
    const code = prompt('Section code (e.g. META, SCORES, NOTES)');
    if (!code) return;
    const title = prompt('Section title');
    if (!title) return;

    try {
      await apiPost(`/super/instruments/${defInstrument.id}/sections`, {
        code: code.trim(),
        title: title.trim(),
        sort_order: 10,
        owner_roles: ['teacher', 'admin'],
        is_active: 1,
      });
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to add section');
    }
  }

  async function updateSection(sec) {
    const title = prompt('New title', sec.title || '');
    if (title == null) return;

    const rolesRaw = prompt(
      'Owner roles CSV (parent,teacher,admin). Example: parent,teacher,admin',
      (sec.owner_roles || []).join(',')
    );
    if (rolesRaw == null) return;
    const roles = rolesRaw
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);

    try {
      await apiPut(`/super/instrument-sections/${sec.id}`, {
        title: title.trim(),
        sort_order: Number(sec.sort_order || 0),
        owner_roles: roles,
        is_active: sec.is_active ? 1 : 0,
      });
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to update section');
    }
  }

  async function addItem(sec) {
    const item_code = prompt('Item code (unique in section)');
    if (!item_code) return;

    const item_type = prompt(
      'Item type: text | textarea | number | date | select | yesno | checkbox | likert3 | likert5',
      'likert3'
    );
    if (!item_type) return;

    const label = prompt('Label (short)', item_code) || item_code;
    const prompt_text = prompt('Prompt text (optional)', '') || null;

    let options = null;
    if (['select', 'checkbox', 'likert3', 'likert5', 'yesno'].includes(item_type)) {
      const opt = prompt('Options JSON (optional). Example: ["Never","Sometimes","Often"]', '');
      if (opt && opt.trim()) {
        try { options = JSON.parse(opt); } catch { alert('Invalid JSON'); return; }
      }
    }

    let scoring_map = null;
    if (['select', 'checkbox', 'likert3', 'likert5', 'yesno'].includes(item_type)) {
      const sm = prompt('Scoring map JSON (optional). Example: {"Never":0,"Sometimes":1,"Often":2}', '');
      if (sm && sm.trim()) {
        try { scoring_map = JSON.parse(sm); } catch { alert('Invalid JSON'); return; }
      }
    }

    try {
      await apiPost(`/super/instrument-sections/${sec.id}/items`, {
        item_code: item_code.trim(),
        item_type: item_type.trim(),
        label,
        prompt_text,
        help_text: null,
        required: 0,
        options,
        scoring_map,
        sort_order: 10,
        is_active: 1,
        external_ref: null,
      });
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to add item');
    }
  }

  function openItemEdit(it) {
    setItemForm({
      ...emptyItemForm,
      id: it.id,
      section_id: it.section_id,
      item_code: it.item_code || '',
      item_type: it.item_type || 'text',
      label: it.label || '',
      prompt_text: it.prompt_text || '',
      help_text: it.help_text || '',
      required: it.required ? 1 : 0,
      options: safeJsonString(it.options),
      scoring_map: safeJsonString(it.scoring_map),
      sort_order: Number(it.sort_order || 0),
      is_active: it.is_active ? 1 : 0,
      external_ref: it.external_ref || '',
    });
    setItemEditOpen(true);
  }

  async function saveItem() {
    if (!itemForm.id) return;
    setItemBusy(true);
    try {
      let options = null;
      let scoring_map = null;

      const optRaw = (itemForm.options || '').trim();
      if (optRaw) options = JSON.parse(optRaw);

      const smRaw = (itemForm.scoring_map || '').trim();
      if (smRaw) scoring_map = JSON.parse(smRaw);

      await apiPut(`/super/instrument-items/${itemForm.id}`, {
        label: itemForm.label || null,
        prompt_text: itemForm.prompt_text || null,
        help_text: itemForm.help_text || null,
        required: itemForm.required ? 1 : 0,
        options,
        scoring_map,
        sort_order: Number(itemForm.sort_order || 0),
        is_active: itemForm.is_active ? 1 : 0,
        external_ref: itemForm.external_ref || null,
      });

      setItemEditOpen(false);
      await reloadDefinition();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || 'Failed to save item (check JSON fields)');
    } finally {
      setItemBusy(false);
    }
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Neutral header */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">
              Global Instruments & Intakes
            </div>
            <div className="mt-1 text-sm text-slate-600">
              Super admin catalog (GLOBAL templates, organization_id = NULL). Licensed gating happens via licenses.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
            <Button variant="secondary" onClick={load} disabled={loading}>
              {loading ? 'Loading…' : 'Reload'}
            </Button>
            <Button variant="primary" onClick={openCreate} disabled={loading}>
              + New Global Template
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
          <div className="lg:col-span-5">
            <label className="text-xs text-slate-600">Search</label>
            <input
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              placeholder="Search by code/name/discipline/provider…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <div className="lg:col-span-3">
            <label className="text-xs text-slate-600">Kind</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={formKind}
              onChange={(e) => setFormKind(e.target.value)}
            >
              <option value="all">All</option>
              <option value="instrument">Instrument</option>
              <option value="intake">Intake</option>
            </select>
          </div>

          <div className="lg:col-span-4">
            <label className="text-xs text-slate-600">Status</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={activeFilter}
              onChange={(e) => setActiveFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="mt-4 bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {loading ? 'Loading…' : `${filtered.length} items`}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="p-3">Code</th>
                <th className="p-3">Name</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Discipline</th>
                <th className="p-3">Type</th>
                <th className="p-3">Distribution</th>
                <th className="p-3">License</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b hover:bg-slate-50">
                  <td className="p-3 font-mono text-[12px]">
                    {it.instrument_code} <span className="text-slate-500">v{it.version}</span>
                  </td>
                  <td className="p-3">
                    <div className="font-medium text-slate-900">{it.name}</div>
                    {it.description ? <div className="text-xs text-slate-500 line-clamp-1">{it.description}</div> : null}
                  </td>
                  <td className="p-3">
                    <Badge tone={it.form_kind === 'intake' ? 'purple' : 'blue'}>{it.form_kind}</Badge>
                  </td>
                  <td className="p-3">
                    {it.discipline_code ? <Badge tone="slate">{it.discipline_code}</Badge> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-3">
                    <Badge tone={it.instrument_type === 'licensed' ? 'amber' : it.instrument_type === 'shell' ? 'slate' : 'green'}>
                      {it.instrument_type}
                    </Badge>
                    {it.license_provider ? <div className="text-xs text-slate-500 mt-1">{it.license_provider}</div> : null}
                  </td>
                  <td className="p-3">
                    <Badge tone={it.distribution_mode === 'shell_only' ? 'slate' : 'green'}>
                      {it.distribution_mode}
                    </Badge>
                  </td>
                  <td className="p-3">
                    {it.license_required ? <Badge tone="amber">required</Badge> : <Badge tone="green">free</Badge>}
                  </td>
                  <td className="p-3">
                    {it.is_active ? <Badge tone="green">active</Badge> : <Badge tone="red">inactive</Badge>}
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="secondary" onClick={() => openDefinition(it)}>
                        Definition
                      </Button>
                      <Button variant="secondary" onClick={() => openEdit(it)}>
                        Edit
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && !loading ? (
                <tr>
                  <td className="p-6 text-center text-slate-500" colSpan={9}>
                    No items found.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT TEMPLATE MODAL */}
      <Modal
        open={editOpen}
        title={form.id ? 'Edit Global Template' : 'Create Global Template'}
        onClose={() => setEditOpen(false)}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-600">Instrument code</label>
            <input className="w-full px-3 py-2 rounded-xl border"
              value={form.instrument_code}
              onChange={(e) => setForm((p) => ({ ...p, instrument_code: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs text-slate-600">Version</label>
            <input type="number" className="w-full px-3 py-2 rounded-xl border"
              value={form.version}
              onChange={(e) => setForm((p) => ({ ...p, version: Number(e.target.value || 1) }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Name</label>
            <input className="w-full px-3 py-2 rounded-xl border"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Description</label>
            <textarea className="w-full px-3 py-2 rounded-xl border min-h-[90px]"
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600">Form kind</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={form.form_kind}
              onChange={(e) => setForm((p) => ({ ...p, form_kind: e.target.value }))}
            >
              <option value="instrument">instrument</option>
              <option value="intake">intake</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-600">Discipline code</label>
            <input className="w-full px-3 py-2 rounded-xl border"
              placeholder="e.g. PROG_PI"
              value={form.discipline_code}
              onChange={(e) => setForm((p) => ({ ...p, discipline_code: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600">Instrument type</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={form.instrument_type}
              onChange={(e) => setForm((p) => ({ ...p, instrument_type: e.target.value }))}
            >
              <option value="shell">shell</option>
              <option value="licensed">licensed</option>
              <option value="custom">custom</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-600">Distribution</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={form.distribution_mode}
              onChange={(e) => setForm((p) => ({ ...p, distribution_mode: e.target.value }))}
            >
              <option value="shell_only">shell_only</option>
              <option value="full_content">full_content</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-600">License required</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={form.license_required ? 1 : 0}
              onChange={(e) => setForm((p) => ({ ...p, license_required: Number(e.target.value) }))}
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-600">License provider</label>
            <input className="w-full px-3 py-2 rounded-xl border"
              placeholder="e.g. DECA, Project ImPACT…"
              value={form.license_provider}
              onChange={(e) => setForm((p) => ({ ...p, license_provider: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600">Active</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={form.is_active ? 1 : 0}
              onChange={(e) => setForm((p) => ({ ...p, is_active: Number(e.target.value) }))}
            >
              <option value={1}>active</option>
              <option value={0}>inactive</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="primary" disabled={loading} onClick={saveInstrument}>
            {loading ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </Modal>

      {/* DEFINITION MODAL */}
      <Modal
        open={defOpen}
        wide
        title={`Definition: ${defInstrument?.instrument_code || ''} v${defInstrument?.version || ''}`}
        onClose={() => setDefOpen(false)}
      >
        {defLoading ? (
          <div className="text-slate-600">Loading definition…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-1 border rounded-2xl p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-slate-900">Sections</div>
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={reloadDefinition}>Reload</Button>
                  <Button variant="primary" onClick={addSection}>+ Section</Button>
                </div>
              </div>

              <div className="mt-3 space-y-2">
                {defSections.map((s) => (
                  <div key={s.id} className="border rounded-xl p-2 hover:bg-slate-50">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-mono text-[12px] text-slate-700">{s.code}</div>
                        <div className="font-medium text-slate-900">{s.title}</div>
                        <div className="text-xs text-slate-500">
                          roles: {(s.owner_roles || []).join(', ') || '—'}
                        </div>
                        <div className="text-xs text-slate-500">
                          sort: {s.sort_order ?? 0} • {s.is_active ? 'active' : 'inactive'}
                        </div>
                      </div>
                      <div className="flex flex-col gap-2 items-end">
                        <Button variant="secondary" className="px-2 py-1 rounded-lg text-xs" onClick={() => addItem(s)}>
                          + Item
                        </Button>
                        <Button variant="secondary" className="px-2 py-1 rounded-lg text-xs" onClick={() => updateSection(s)}>
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {!defSections.length ? (
                  <div className="text-sm text-slate-500">No sections yet. Add a section.</div>
                ) : null}
              </div>
            </div>

            <div className="lg:col-span-2 border rounded-2xl p-3">
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">Items</div>
                <div className="text-xs text-slate-500">{defItems.length} items</div>
              </div>

              <div className="mt-2 overflow-x-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="p-2 text-left">Section</th>
                      <th className="p-2 text-left">Code</th>
                      <th className="p-2 text-left">Type</th>
                      <th className="p-2 text-left">Label</th>
                      <th className="p-2 text-left">Required</th>
                      <th className="p-2 text-left">Active</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {defItems.map((it) => (
                      <tr key={it.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 text-slate-600">
                          {defSections.find((s) => s.id === it.section_id)?.code || it.section_id}
                        </td>
                        <td className="p-2 font-mono text-[12px]">{it.item_code}</td>
                        <td className="p-2"><Badge>{it.item_type}</Badge></td>
                        <td className="p-2">{it.label || '—'}</td>
                        <td className="p-2">{it.required ? 'Yes' : 'No'}</td>
                        <td className="p-2">{it.is_active ? <Badge tone="green">active</Badge> : <Badge tone="red">inactive</Badge>}</td>
                        <td className="p-2 text-right">
                          <Button
                            variant="secondary"
                            className="px-2 py-1 rounded-lg text-xs"
                            onClick={() => openItemEdit(it)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {!defItems.length ? (
                      <tr><td colSpan={7} className="p-4 text-slate-500 text-center">No items.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 text-xs text-slate-500">
                Item edit uses <span className="font-mono">PUT /api/super/instrument-items/:id</span>.
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ITEM EDIT MODAL */}
      <Modal
        open={itemEditOpen}
        title={`Edit item: ${itemForm.item_code || ''}`}
        onClose={() => { if (!itemBusy) setItemEditOpen(false); }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-600">Item code</label>
            <input className="w-full px-3 py-2 rounded-xl border bg-slate-50"
              value={itemForm.item_code}
              disabled
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Code/type are not edited here (kept stable). Edit label/prompt/options/scoring.
            </div>
          </div>

          <div>
            <label className="text-xs text-slate-600">Item type</label>
            <input className="w-full px-3 py-2 rounded-xl border bg-slate-50"
              value={itemForm.item_type}
              disabled
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Label</label>
            <input className="w-full px-3 py-2 rounded-xl border"
              value={itemForm.label}
              onChange={(e) => setItemForm((p) => ({ ...p, label: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Prompt text</label>
            <textarea className="w-full px-3 py-2 rounded-xl border min-h-[80px]"
              value={itemForm.prompt_text}
              onChange={(e) => setItemForm((p) => ({ ...p, prompt_text: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Help text</label>
            <textarea className="w-full px-3 py-2 rounded-xl border min-h-[70px]"
              value={itemForm.help_text}
              onChange={(e) => setItemForm((p) => ({ ...p, help_text: e.target.value }))}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600">Sort order</label>
            <input type="number" className="w-full px-3 py-2 rounded-xl border"
              value={itemForm.sort_order}
              onChange={(e) => setItemForm((p) => ({ ...p, sort_order: Number(e.target.value || 0) }))}
            />
          </div>

          <div>
            <label className="text-xs text-slate-600">Required</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={itemForm.required ? 1 : 0}
              onChange={(e) => setItemForm((p) => ({ ...p, required: Number(e.target.value) }))}
            >
              <option value={0}>No</option>
              <option value={1}>Yes</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-600">Active</label>
            <select className="w-full px-3 py-2 rounded-xl border"
              value={itemForm.is_active ? 1 : 0}
              onChange={(e) => setItemForm((p) => ({ ...p, is_active: Number(e.target.value) }))}
            >
              <option value={1}>active</option>
              <option value={0}>inactive</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-slate-600">External ref</label>
            <input className="w-full px-3 py-2 rounded-xl border"
              value={itemForm.external_ref}
              onChange={(e) => setItemForm((p) => ({ ...p, external_ref: e.target.value }))}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Options JSON</label>
            <textarea className="w-full px-3 py-2 rounded-xl border font-mono text-[12px] min-h-[90px]"
              value={itemForm.options}
              onChange={(e) => setItemForm((p) => ({ ...p, options: e.target.value }))}
              placeholder='e.g. ["Never","Sometimes","Often"]'
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Leave empty for non-choice items. Must be valid JSON if provided.
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs text-slate-600">Scoring map JSON</label>
            <textarea className="w-full px-3 py-2 rounded-xl border font-mono text-[12px] min-h-[90px]"
              value={itemForm.scoring_map}
              onChange={(e) => setItemForm((p) => ({ ...p, scoring_map: e.target.value }))}
              placeholder='e.g. {"Never":0,"Sometimes":1,"Often":2}'
            />
            <div className="text-[11px] text-slate-500 mt-1">
              Leave empty to disable scoring for this item. Must be valid JSON if provided.
            </div>
          </div>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setItemEditOpen(false)} disabled={itemBusy}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveItem} disabled={itemBusy}>
            {itemBusy ? 'Saving…' : 'Save item'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}



